import {
  applyAction, DEFAULT_RULES, GameError, type Action, type GameState, type Notice, type Rules,
} from '@thicket/core';
import { HttpError, NetworkError, request, type Fetch } from './api.js';

export interface Queued {
  /** Sent as the Idempotency-Key, so a retry can never apply twice. */
  key: string;
  action: Action;
  queuedAt: string;
}

export interface StoreDeps {
  fetch: Fetch;
  /** Where the offline queue is kept between visits; may be unavailable. */
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  uuid: () => string;
  now: () => Date;
  /** Called after a sign-in is needed. */
  onSignedOut: () => void;
  /** Rewards and other news from the server, or a plain message to show. */
  onNotices: (notices: Notice[]) => void;
  onMessage: (message: string) => void;
  /** Schedules a retry; returns nothing. */
  later: (fn: () => void, ms: number) => void;
}

/** What the screens use from a store: the real one (synced) or dev mode's sandbox. */
export interface GameStore {
  readonly view: GameState | null;
  readonly rules: Rules;
  readonly queue: readonly unknown[];
  readonly offline: boolean;
  /** The game's clock: real time, or dev mode's time-travelled clock. */
  now(): Date;
  subscribe(fn: () => void): () => void;
  load(): Promise<void>;
  refresh(): Promise<void>;
  flush(): Promise<void>;
  onRemoteVersion(version: number): void;
  act(input: Action): boolean;
}

/**
 * Keeps the game in sync. `confirmed` is the last state the server sent;
 * `view` is that plus every queued action, applied with the same engine the
 * server uses, so a tap shows up at once and offline taps aren't lost.
 */
export class Store implements GameStore {
  confirmed: GameState | null = null;
  view: GameState | null = null;
  version = 0;
  rules: Rules = DEFAULT_RULES;
  queue: Queued[] = [];
  syncing = false;
  offline = false;
  private listeners = new Set<() => void>();
  private retryScheduled = false;

  constructor(private deps: StoreDeps) {}

  now(): Date {
    return this.deps.now();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  private get queueKey() {
    return this.confirmed ? `thicket.queue.${this.confirmed.profile.id}` : null;
  }

  private persist() {
    const key = this.queueKey;
    if (!key || !this.deps.storage) return;
    try {
      if (this.queue.length) this.deps.storage.setItem(key, JSON.stringify(this.queue));
      else this.deps.storage.removeItem(key);
    } catch {
      // Storage full or blocked: the queue still lives in memory.
    }
  }

  private restoreQueue() {
    const key = this.queueKey;
    if (!key || !this.deps.storage) return;
    try {
      const saved = JSON.parse(this.deps.storage.getItem(key) ?? '[]');
      if (Array.isArray(saved)) this.queue = saved;
    } catch {
      this.queue = [];
    }
  }

  private context() {
    // Previews never roll a seed; the server's reply has the real reward.
    return { now: this.deps.now(), newId: this.deps.uuid, random: () => 1, rules: this.rules, source: 'app' as const };
  }

  /** Recomputes `view` from the confirmed state plus the queue. */
  private recompute() {
    let state = this.confirmed;
    if (state) {
      for (const q of this.queue) {
        try {
          state = applyAction(state, q.action, this.context()).state;
        } catch {
          // The server will reject it too; it's dropped when it gets there.
        }
      }
    }
    this.view = state;
    this.emit();
  }

  private async call<T>(path: string, init?: RequestInit): Promise<T> {
    try {
      const result = await request<T>(this.deps.fetch, path, init);
      if (this.offline) {
        this.offline = false;
        this.emit();
      }
      return result;
    } catch (err) {
      if (err instanceof HttpError && err.status === 401) this.deps.onSignedOut();
      if (err instanceof NetworkError && !this.offline) {
        this.offline = true;
        this.emit();
      }
      throw err;
    }
  }

  /** The last state the server sent, so the app can open with no connection. */
  private saveSnapshot() {
    if (!this.deps.storage || !this.confirmed) return;
    try {
      this.deps.storage.setItem('thicket.snapshot', JSON.stringify({ version: this.version, state: this.confirmed, rules: this.rules }));
    } catch {
      // Too big or blocked; the app just needs a connection to open next time.
    }
  }

  async load(): Promise<void> {
    let loaded: { version: number; state: GameState };
    try {
      const [rules, state] = await Promise.all([
        this.call<Rules>('/api/rules').catch((err) => {
          if (err instanceof NetworkError) throw err;
          return DEFAULT_RULES;
        }),
        this.call<{ version: number; state: GameState }>('/api/state'),
      ]);
      this.rules = rules;
      loaded = state;
    } catch (err) {
      const saved = err instanceof NetworkError && this.deps.storage?.getItem('thicket.snapshot');
      if (!saved) throw err;
      const snap = JSON.parse(saved) as { version: number; state: GameState; rules: Rules };
      this.rules = snap.rules;
      loaded = snap;
    }
    this.confirmed = loaded.state;
    this.version = loaded.version;
    this.restoreQueue();
    this.saveSnapshot();
    this.recompute();
    void this.flush();
  }

  /** Fetches the latest state, after sending anything queued. */
  async refresh(): Promise<void> {
    if (this.queue.length) return this.flush();
    try {
      const loaded = await this.call<{ version: number; state: GameState }>('/api/state');
      this.confirmed = loaded.state;
      this.version = loaded.version;
      this.saveSnapshot();
      this.recompute();
    } catch {
      // Stay on what we have; the next refresh will try again.
    }
  }

  /** The server announced a new version (another device, or Claude). */
  onRemoteVersion(version: number) {
    if (version > this.version && !this.queue.length) void this.refresh();
  }

  /**
   * Does something. It shows right away; if the engine refuses it, nothing is
   * queued and the reason is shown instead.
   */
  act(input: Action): boolean {
    if (!this.view) return false;
    const action = structuredClone(input);
    // A task made offline needs an id the server will keep, so later taps on it still match.
    if (action.type === 'createTask' && !action.task.id) action.task.id = this.deps.uuid();
    try {
      applyAction(this.view, action, this.context());
    } catch (err) {
      this.deps.onMessage(err instanceof GameError ? err.message : 'That didn’t work');
      return false;
    }
    this.queue.push({ key: this.deps.uuid(), action, queuedAt: this.deps.now().toISOString() });
    this.persist();
    this.recompute();
    void this.flush();
    return true;
  }

  /** Sends queued actions one at a time, in order. Stops at the first connection problem. */
  async flush(): Promise<void> {
    if (this.syncing || !this.queue.length) return;
    this.syncing = true;
    this.emit();
    try {
      while (this.queue.length) {
        const q = this.queue[0]!;
        let res: { version: number; state: GameState; notices: Notice[] };
        try {
          res = await this.call('/api/actions', {
            method: 'POST',
            headers: { 'Idempotency-Key': q.key },
            body: JSON.stringify({ action: q.action }),
          });
        } catch (err) {
          if (err instanceof HttpError && err.status >= 400 && err.status < 500 && err.status !== 401 && err.status !== 429) {
            // The game said no (it changed elsewhere first). Drop it and say why.
            this.queue.shift();
            this.persist();
            this.deps.onMessage(err.message);
            continue;
          }
          this.scheduleRetry();
          break;
        }
        this.queue.shift();
        this.persist();
        this.confirmed = res.state;
        this.version = res.version;
        if (res.notices?.length) this.deps.onNotices(res.notices);
      }
    } finally {
      this.syncing = false;
      this.saveSnapshot();
      this.recompute();
    }
  }

  private scheduleRetry() {
    if (this.retryScheduled) return;
    this.retryScheduled = true;
    this.deps.later(() => {
      this.retryScheduled = false;
      void this.flush();
    }, 5000);
  }
}
